import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PaymentDashboard,
  PaymentRequest,
  Payment,
  PaymentConfirmation,
  PaymentHistory,
  ReturnSummary,
  ReturnDetail,
  ReturnUpdate,
  InstallmentPlan,
  SavedPaymentMethod,
  CreateScheduledPayment,
  ScheduledPayment,
  GatewayStatusInfo,
  CheckoutStartResponse
} from '../../model/payment/payment.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private apiUrl = `${environment.apiUrl}/external/payments`;

  constructor(private http: HttpClient) { }

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS'
    })
  };

  /**
   * Get payment dashboard data for the current account
   * Includes outstanding returns, recent payments, and saved payment methods
   */
  getDashboard(): Observable<PaymentDashboard> {
    return this.http.get<PaymentDashboard>(`${this.apiUrl}/dashboard`, this.httpOptions);
  }

  /**
   * Get all outstanding returns for the current account
   */
  getOutstandingReturns(): Observable<ReturnSummary[]> {
    return this.http.get<ReturnSummary[]>(`${this.apiUrl}/outstanding-returns`, this.httpOptions);
  }

  /**
   * Create the next upcoming return(s) for the current account. The backend determines the
   * period from the account's latest return and its filing frequency. Returns how many were
   * actually created (already-existing periods are skipped).
   */
  createNextReturns(count: number = 1): Observable<{ created: number }> {
    const params = new HttpParams().set('count', count.toString());
    return this.http.post<{ created: number }>(
      `${this.apiUrl}/returns/next`, {}, { ...this.httpOptions, params }
    );
  }

  /**
   * Get details for a specific return
   */
  getReturnById(returnId: number): Observable<ReturnSummary> {
    return this.http.get<ReturnSummary>(`${this.apiUrl}/returns/${returnId}`, this.httpOptions);
  }

  /**
   * Get the full editable detail of a return (per-property rows + calculation breakdown).
   */
  getReturnDetail(returnId: number): Observable<ReturnDetail> {
    return this.http.get<ReturnDetail>(`${this.apiUrl}/returns/${returnId}/detail`, this.httpOptions);
  }

  /**
   * Save edited gross-rental-receipts / exemptions; backend recalculates totals.
   */
  updateReturn(returnId: number, update: ReturnUpdate): Observable<ReturnDetail> {
    return this.http.put<ReturnDetail>(`${this.apiUrl}/returns/${returnId}`, update, this.httpOptions);
  }

  /**
   * Installment plan for a return that is paid in installments (if any).
   * Backend endpoint pending; callers should treat errors/empty as "no plan".
   */
  getInstallmentPlan(returnId: number): Observable<InstallmentPlan> {
    return this.http.get<InstallmentPlan>(`${this.apiUrl}/returns/${returnId}/installment-plan`, this.httpOptions);
  }

  /**
   * Process a payment for selected returns
   */
  processPayment(request: PaymentRequest): Observable<PaymentConfirmation> {
    return this.http.post<PaymentConfirmation>(`${this.apiUrl}/process`, request, this.httpOptions);
  }

  /**
   * Get the active payment processor and whether it supports hosted checkout (redirect to the
   * gateway's own payment page instead of collecting card details in-app).
   */
  getGatewayStatus(): Observable<GatewayStatusInfo> {
    return this.http.get<GatewayStatusInfo>(`${this.apiUrl}/gateway-status`, this.httpOptions);
  }

  /**
   * Start a hosted-checkout payment for the selected returns. Returns the gateway's checkout
   * URL to redirect the browser to (full return balance only — no partial/installment amount).
   */
  startCheckout(returnIds: number[], returnUrl?: string): Observable<CheckoutStartResponse> {
    // Only sent when there is one: the portal wants the gateway's default landing page.
    const body = returnUrl ? { returnIds, returnUrl } : { returnIds };
    return this.http.post<CheckoutStartResponse>(
      `${this.apiUrl}/checkout/start`, body, this.httpOptions
    );
  }

  /**
   * Complete a hosted-checkout payment after the taxpayer returns from the gateway. While the
   * gateway is still confirming the transaction, this rejects with a "still processing" error —
   * callers should retry after a short delay.
   */
  completeCheckout(transactionId: string, ref: number | null): Observable<PaymentConfirmation> {
    return this.http.post<PaymentConfirmation>(
      `${this.apiUrl}/checkout/complete`, { transactionId, ref }, this.httpOptions
    );
  }

  /**
   * The taxpayer left the hosted checkout (closed the window / back button). The server re-checks
   * the gateway: an approval that already happened is finalized (status COMPLETED), otherwise the
   * pending payment is cancelled on our side (status CANCELLED).
   */
  cancelCheckout(transactionId: string): Observable<{ status: 'COMPLETED' | 'CANCELLED'; confirmationNumber: string | null }> {
    return this.http.post<{ status: 'COMPLETED' | 'CANCELLED'; confirmationNumber: string | null }>(
      `${this.apiUrl}/checkout/cancel`, { transactionId }, this.httpOptions
    );
  }

  /**
   * Calculate processing fees for a payment amount and method
   */
  calculateFees(amount: number, paymentMethodType: string): Observable<{ processingFee: number; total: number }> {
    const params = new HttpParams()
      .set('amount', amount.toString())
      .set('paymentMethodType', paymentMethodType);

    return this.http.get<{ processingFee: number; total: number }>(
      `${this.apiUrl}/calculate-fees`,
      { ...this.httpOptions, params }
    );
  }

  /**
   * Get payment history with pagination
   */
  getPaymentHistory(page: number = 0, pageSize: number = 20): Observable<PaymentHistory> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', pageSize.toString());

    return this.http.get<PaymentHistory>(`${this.apiUrl}/history`, { ...this.httpOptions, params });
  }

  /**
   * Get details for a specific payment
   */
  getPaymentById(paymentId: number): Observable<Payment> {
    return this.http.get<Payment>(`${this.apiUrl}/${paymentId}`, this.httpOptions);
  }

  /**
   * Get payment by confirmation number
   */
  getPaymentByConfirmation(confirmationNumber: string): Observable<Payment> {
    return this.http.get<Payment>(
      `${this.apiUrl}/confirmation/${confirmationNumber}`,
      this.httpOptions
    );
  }

  /**
   * Get all saved payment methods
   */
  getSavedPaymentMethods(): Observable<SavedPaymentMethod[]> {
    return this.http.get<SavedPaymentMethod[]>(`${this.apiUrl}/payment-methods`, this.httpOptions);
  }

  /**
   * Add a new payment method
   */
  addPaymentMethod(paymentMethod: SavedPaymentMethod): Observable<SavedPaymentMethod> {
    return this.http.post<SavedPaymentMethod>(
      `${this.apiUrl}/payment-methods`,
      paymentMethod,
      this.httpOptions
    );
  }

  /**
   * Update a payment method
   */
  updatePaymentMethod(id: number, paymentMethod: SavedPaymentMethod): Observable<SavedPaymentMethod> {
    return this.http.put<SavedPaymentMethod>(
      `${this.apiUrl}/payment-methods/${id}`,
      paymentMethod,
      this.httpOptions
    );
  }

  /**
   * Delete a saved payment method
   */
  deletePaymentMethod(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/payment-methods/${id}`, this.httpOptions);
  }

  /**
   * Set a payment method as default
   */
  setDefaultPaymentMethod(id: number): Observable<SavedPaymentMethod> {
    return this.http.put<SavedPaymentMethod>(
      `${this.apiUrl}/payment-methods/${id}/set-default`,
      {},
      this.httpOptions
    );
  }

  // ===== Scheduled payments =====

  /** Schedule a future-dated payment with a saved payment method. */
  createScheduledPayment(request: CreateScheduledPayment): Observable<ScheduledPayment> {
    return this.http.post<ScheduledPayment>(`${this.apiUrl}/scheduled`, request, this.httpOptions);
  }

  /** All of the account's scheduled payments (pending, processed, failed, cancelled). */
  listScheduledPayments(): Observable<ScheduledPayment[]> {
    return this.http.get<ScheduledPayment[]>(`${this.apiUrl}/scheduled`, this.httpOptions);
  }

  /** Cancel a pending scheduled payment. */
  cancelScheduledPayment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/scheduled/${id}`, this.httpOptions);
  }

  /** Receipt endpoint URL — native saves the PDF outside HttpClient (see NativeIoService.saveDownload). */
  receiptUrl(paymentId: number): string {
    return `${this.apiUrl}/${paymentId}/receipt`;
  }

  /**
   * Download payment receipt as PDF
   */
  downloadReceipt(paymentId: number): Observable<Blob> {
    return this.http.get(this.receiptUrl(paymentId), {
      responseType: 'blob'
    });
  }

  /**
   * Send payment receipt via email
   */
  emailReceipt(paymentId: number, emailAddress: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${paymentId}/email-receipt`,
      { emailAddress },
      this.httpOptions
    );
  }
}
